import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Download, TrendingUp, DollarSign, LayoutDashboard, GitPullRequest, X } from "lucide-react";
import api from "@/lib/api";
import ProfessionalProfile from "./ProfessionalProfile";
import ChangeRequestsPanel from "./ChangeRequestsPanel";
import { StatusBadge } from "./NetworkCommand";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");

const STAT_CARDS = [
  { key: "total_registered", label: "Total Registered" },
  { key: "pending_applications", label: "Pending Applications", filter: "pending", accent: "text-amber-600" },
  { key: "approved", label: "Approved", filter: "approved", accent: "text-emerald-600" },
  { key: "denied", label: "Denied", filter: "denied", accent: "text-red-600" },
  { key: "suspended", label: "Suspended", filter: "suspended", accent: "text-orange-600" },
  { key: "inactive", label: "Inactive", filter: "inactive" },
  { key: "active_today", label: "Active Today", flag: "active_today" },
  { key: "available_now", label: "Available Now", flag: "available_now", accent: "text-emerald-600" },
  { key: "credential_issues", label: "Credential Issues", flag: "credential_issues", accent: "text-red-600" },
  { key: "pending_documents", label: "Pending Documents", flag: "pending_documents", accent: "text-amber-600" },
  { key: "current_assignments", label: "Current Assignments", flag: "has_active_assignments" },
  { key: "completed_assignments", label: "Completed Assignments", flag: "has_completed_assignments" },
  { key: "pending_change_requests", label: "Pending Changes", tab: "changes", accent: "text-cobalt" },
  { key: "pending_payouts", label: "Pending Payouts", money: true, tab: "payouts", accent: "text-amber-600" },
  { key: "lifetime_payouts", label: "Lifetime Payouts", money: true, tab: "payouts", accent: "text-emerald-600" },
];

export default function CommandCenterView({ network, meta, onNavigate }) {
  const [stats, setStats] = useState(null);
  const [subtab, setSubtab] = useState("operations");
  const [drill, setDrill] = useState(null); // {status, flag, label}
  const [openId, setOpenId] = useState(null);

  const loadStats = () => api.get(`/network-command/${network}/stats`).then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { setStats(null); setDrill(null); setSubtab("operations"); loadStats(); }, [network]);

  if (!stats) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  const onCard = (c) => {
    if (c.tab) { setSubtab(c.tab); return; }
    setSubtab("operations");
    if (c.filter) setDrill({ status: c.filter, flag: null, label: c.label });
    else if (c.flag) setDrill({ status: null, flag: c.flag, label: c.label });
    else setDrill(null);
  };
  const isActive = (c) => drill && ((c.filter && drill.status === c.filter && !drill.flag) || (c.flag && drill.flag === c.flag));

  const subtabLabel = { operations: null, payouts: "Payouts", analytics: "Analytics", changes: "Change Requests" }[subtab];
  const crumb = subtab === "operations" ? (drill?.label || null) : subtabLabel;

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-1.5 text-xs font-medium text-slate-400" data-testid="nc-breadcrumbs">
        <button onClick={() => onNavigate?.("overview")} data-testid="nc-breadcrumb-admin" className="hover:text-navy transition-colors">Admin</button>
        <span>›</span>
        <button onClick={() => { setDrill(null); setSubtab("operations"); }} data-testid="nc-breadcrumb-network"
          className={`transition-colors ${crumb ? "hover:text-navy" : "text-navy font-semibold"}`}>{meta.label}</button>
        {crumb && (<><span>›</span><span className="text-navy font-semibold" data-testid="nc-breadcrumb-current">{crumb}</span></>)}
      </nav>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className={`font-heading text-2xl font-extrabold text-navy`} data-testid={`nc-title-${network}`}>{meta.label}</h2>
          <p className="text-sm text-slate-500">National {stats.label} network operations — recruit, approve, monitor, compensate.</p>
        </div>
        <div className="flex gap-2">
          {[{ k: "operations", l: "Operations", I: LayoutDashboard }, { k: "changes", l: "Changes", I: GitPullRequest }, { k: "payouts", l: "Payouts", I: DollarSign }, { k: "analytics", l: "Analytics", I: TrendingUp }].map(({ k, l, I }) => (
            <button key={k} onClick={() => setSubtab(k)} data-testid={`nc-subtab-${k}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${subtab === k ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              <I className="w-4 h-4" /> {l}
              {k === "changes" && stats.pending_change_requests > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-cobalt text-white text-[10px] font-bold" data-testid="nc-changes-badge">{stats.pending_change_requests}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-8 gap-3">
        {STAT_CARDS.map((c) => (
          <button key={c.key} onClick={() => onCard(c)} data-testid={`nc-stat-${c.key}`}
            className={`text-left bg-white rounded-2xl border p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all hover:border-cobalt hover:-translate-y-0.5 ${isActive(c) ? "border-cobalt ring-2 ring-cobalt/20" : "border-slate-100"}`}>
            <div className={`font-heading text-xl font-extrabold ${c.accent || "text-navy"}`}>
              {c.money ? money(stats[c.key]) : stats[c.key] ?? 0}
            </div>
            <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mt-1 leading-tight">{c.label}</div>
          </button>
        ))}
      </div>

      {subtab === "operations" && (
        <WorkQueue network={network} drill={drill} setDrill={setDrill} onOpen={setOpenId} />
      )}
      {subtab === "changes" && <ChangeRequestsPanel network={network} />}
      {subtab === "payouts" && <PayoutCenter network={network} onChanged={loadStats} />}
      {subtab === "analytics" && <AnalyticsPanel network={network} />}

      {openId && (
        <ProfessionalProfile network={network} memberId={openId}
          onClose={() => setOpenId(null)} onChanged={loadStats} />
      )}
    </div>
  );
}

function WorkQueue({ network, drill, setDrill, onOpen }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [sort, setSort] = useState("applied_at");
  const [order, setOrder] = useState("desc");
  const [selected, setSelected] = useState({});
  const [bulkAction, setBulkAction] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const statusFilter = drill?.status || null;
  const flag = drill?.flag || null;

  const load = () => {
    const params = { sort, order };
    if (statusFilter) params.status = statusFilter;
    if (flag) params.flag = flag;
    if (q.trim()) params.q = q.trim();
    api.get(`/network-command/${network}/members`, { params }).then(({ data }) => setRows(data.members)).catch(() => setRows([]));
  };
  useEffect(load, [network, statusFilter, flag, sort, order]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [q]);

  const selIds = Object.keys(selected).filter((k) => selected[k]);
  const runBulk = async () => {
    if (!bulkAction || selIds.length === 0) return;
    if (["deny", "suspend"].includes(bulkAction) && !bulkReason.trim()) { toast.error("Reason required for this bulk action"); return; }
    if (!window.confirm(`Apply "${bulkAction}" to ${selIds.length} professional(s)?`)) return;
    setBulkBusy(true);
    try {
      for (const id of selIds) {
        await api.post(`/network-command/${network}/members/${id}/action`, { action: bulkAction, reason: bulkReason || undefined });
      }
      toast.success(`${bulkAction} applied to ${selIds.length} member(s)`);
      setSelected({}); setBulkAction(""); setBulkReason(""); load();
    } catch (e) { toast.error("Bulk action failed"); }
    finally { setBulkBusy(false); }
  };

  const headers = [
    ["name", "Name"], ["organization", "Organization"], ["role_title", "Role"], ["state", "State"],
    ["license", "License"], ["status", "Status"], ["applied_at", "Applied"], ["last_activity", "Last Activity"],
  ];

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="nc-queue-search"
            placeholder="Search name, email, phone, license, state, organization, ID…"
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30" />
        </div>
        <select value={statusFilter || ""} onChange={(e) => setDrill(e.target.value ? { status: e.target.value, flag, label: e.target.value } : (flag ? { status: null, flag, label: drill?.label } : null))} data-testid="nc-queue-status-filter"
          className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white">
          <option value="">All statuses</option>
          {["pending", "approved", "denied", "suspended", "inactive", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {drill && (
          <button onClick={() => setDrill(null)} data-testid="nc-clear-drill"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cobalt/10 border border-cobalt/30 text-cobalt text-xs font-semibold hover:bg-cobalt/20">
            {drill.label || "Filtered"} <X className="w-3 h-3" />
          </button>
        )}
        {selIds.length > 0 && (
          <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-1.5 border border-slate-200" data-testid="nc-bulk-bar">
            <span className="text-xs font-semibold text-navy">{selIds.length} selected</span>
            <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} data-testid="nc-bulk-action"
              className="px-2 py-1 rounded-lg border border-slate-200 text-xs bg-white">
              <option value="">Bulk action…</option>
              <option value="approve">Approve</option><option value="deny">Deny</option>
              <option value="suspend">Suspend</option><option value="reactivate">Reactivate</option>
            </select>
            {["deny", "suspend"].includes(bulkAction) && (
              <input value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} placeholder="Reason (required)"
                data-testid="nc-bulk-reason" className="px-2 py-1 rounded-lg border border-slate-200 text-xs w-40" />
            )}
            <button onClick={runBulk} disabled={bulkBusy} data-testid="nc-bulk-apply"
              className="px-3 py-1 rounded-lg bg-navy text-white text-xs font-semibold disabled:opacity-50">
              {bulkBusy ? "Applying…" : "Apply"}
            </button>
          </div>
        )}
      </div>

      {!rows ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div> : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-2 w-8"><input type="checkbox" data-testid="nc-select-all"
                checked={rows.length > 0 && selIds.length === rows.length}
                onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(rows.map((r) => [r.id, true])) : {})} /></th>
              {headers.map(([k, l]) => (
                <th key={k} className="py-2 pr-4 cursor-pointer select-none hover:text-navy"
                  onClick={() => { if (sort === k) setOrder(order === "desc" ? "asc" : "desc"); else { setSort(k); setOrder("desc"); } }}>
                  {l}{sort === k ? (order === "desc" ? " ↓" : " ↑") : ""}
                </th>
              ))}
              <th className="py-2 pr-4">Reviewer</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" data-testid={`nc-queue-row-${r.id}`}>
                  <td className="py-2.5 pr-2" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={!!selected[r.id]} data-testid={`nc-select-${r.id}`}
                      onChange={(e) => setSelected({ ...selected, [r.id]: e.target.checked })} />
                  </td>
                  <td className="py-2.5 pr-4" onClick={() => onOpen(r.id)}>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-navy/10 flex items-center justify-center text-xs font-bold text-navy shrink-0">
                        {(r.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-navy">{r.name || "—"}</div>
                        <div className="text-xs text-slate-400">{r.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 pr-4" onClick={() => onOpen(r.id)}>{r.organization || "—"}</td>
                  <td className="py-2.5 pr-4" onClick={() => onOpen(r.id)}>{r.role_title}</td>
                  <td className="py-2.5 pr-4" onClick={() => onOpen(r.id)}>{r.state || "—"}</td>
                  <td className="py-2.5 pr-4" onClick={() => onOpen(r.id)}>{r.license || "—"}</td>
                  <td className="py-2.5 pr-4" onClick={() => onOpen(r.id)}><StatusBadge status={r.status} /></td>
                  <td className="py-2.5 pr-4" onClick={() => onOpen(r.id)}>{fmtDate(r.applied_at)}</td>
                  <td className="py-2.5 pr-4" onClick={() => onOpen(r.id)}>{fmtDate(r.last_activity)}</td>
                  <td className="py-2.5 pr-4" onClick={() => onOpen(r.id)}>{r.reviewer || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="py-10 text-center text-sm text-slate-400" data-testid="nc-queue-empty">No professionals match this view.</div>}
        </div>
      )}
    </div>
  );
}

function PayoutCenter({ network, onChanged }) {
  const [data, setData] = useState(null);
  const load = () => api.get(`/network-command/${network}/payouts`).then(({ data }) => setData(data)).catch(() => setData({ summary: {}, payouts: [] }));
  useEffect(() => { load(); }, [network]);

  const exportCsv = async () => {
    const res = await api.get(`/network-command/${network}/payouts`, { params: { export: "csv" }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${network}_payouts.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const setStatus = async (payoutId, status) => {
    try {
      await api.post(`/network-command/${network}/payouts/${payoutId}/status`, { status });
      toast.success(`Payout marked ${status}`); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  if (!data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const supported = ["rn", "drone"].includes(network);

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-heading font-semibold text-navy">Payout Command Center</h3>
        <button onClick={exportCsv} data-testid="nc-payout-export-csv"
          className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[["pending", "Pending", "text-amber-600"], ["paid", "Paid", "text-emerald-600"], ["held", "Held", "text-orange-600"], ["disputed", "Disputed", "text-red-600"]].map(([k, l, c]) => (
          <div key={k} className="bg-slate-50 rounded-2xl p-4" data-testid={`nc-payout-sum-${k}`}>
            <div className={`font-heading text-2xl font-extrabold ${c}`}>{money(data.summary?.[k])}</div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-1">{l}</div>
          </div>
        ))}
      </div>
      {!supported ? (
        <div className="mt-6 py-8 text-center text-sm text-slate-400" data-testid="nc-payout-na">
          Direct payouts are not applicable for this network yet. Stripe Connect &amp; 1099 automation arrive in a future phase.
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-4">Payout</th><th className="py-2 pr-4">Professional</th><th className="py-2 pr-4">Amount</th>
              <th className="py-2 pr-4">Period</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Created</th><th className="py-2 pr-4">Actions</th>
            </tr></thead>
            <tbody>
              {data.payouts.map((p) => (
                <tr key={p.payout_id} className="border-b border-slate-50" data-testid={`nc-payout-row-${p.payout_id}`}>
                  <td className="py-2.5 pr-4 font-mono text-xs">{p.payout_id}</td>
                  <td className="py-2.5 pr-4 font-medium text-navy">{p.name || p.member_id}</td>
                  <td className="py-2.5 pr-4 font-semibold">{money(p.amount)}</td>
                  <td className="py-2.5 pr-4">{p.period || "—"}</td>
                  <td className="py-2.5 pr-4"><StatusBadge status={p.status === "paid" ? "approved" : p.status === "disputed" ? "denied" : "pending"} /> <span className="text-xs text-slate-400">{p.status}</span></td>
                  <td className="py-2.5 pr-4">{fmtDate(p.created_at)}</td>
                  <td className="py-2.5 pr-4">
                    <select defaultValue="" data-testid={`nc-payout-status-${p.payout_id}`}
                      onChange={(e) => { if (e.target.value) { setStatus(p.payout_id, e.target.value); e.target.value = ""; } }}
                      className="px-2 py-1 rounded-lg border border-slate-200 text-xs bg-white">
                      <option value="">Set status…</option>
                      <option value="paid">Mark Paid</option><option value="pending">Mark Pending</option>
                      <option value="held">Hold</option><option value="disputed">Disputed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.payouts.length === 0 && <div className="py-8 text-center text-sm text-slate-400">No payouts recorded yet.</div>}
        </div>
      )}
    </div>
  );
}

function AnalyticsPanel({ network }) {
  const [a, setA] = useState(null);
  useEffect(() => { setA(null); api.get(`/network-command/${network}/analytics`).then(({ data }) => setA(data)).catch(() => {}); }, [network]);
  if (!a) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const max = Math.max(1, ...a.growth.map((g) => g.applications));
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-semibold text-navy">Application Growth (6 months)</h3>
        <div className="mt-6 flex items-end gap-3 h-40" data-testid="nc-analytics-growth">
          {a.growth.map((g) => (
            <div key={g.month} className="flex-1 flex flex-col items-center gap-1">
              <div className="text-xs font-semibold text-navy">{g.applications}</div>
              <div className="w-full rounded-t-lg bg-cobalt/80 transition-all" style={{ height: `${(g.applications / max) * 100}%`, minHeight: 4 }} />
              <div className="text-[10px] text-slate-400">{g.month.slice(5)}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-semibold text-navy">Network Health</h3>
        <div className="mt-4 grid grid-cols-2 gap-4">
          {[["Approval Rate", `${a.approval_rate}%`, "text-emerald-600"], ["Denial Rate", `${a.denial_rate}%`, "text-red-600"],
            ["Avg Review Time", a.avg_review_days != null ? `${a.avg_review_days} days` : "—", "text-navy"],
            ["Utilization", `${a.utilization}%`, "text-cobalt"], ["States Covered", a.states_covered, "text-navy"]].map(([l, v, c]) => (
            <div key={l} className="bg-slate-50 rounded-2xl p-4">
              <div className={`font-heading text-xl font-extrabold ${c}`}>{v}</div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-semibold text-navy">Top Performing Members</h3>
        <div className="mt-3 space-y-2" data-testid="nc-analytics-performers">
          {a.top_performers.map((p, i) => (
            <div key={p.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-slate-400 w-5">#{i + 1}</span>
                <div><div className="text-sm font-medium text-navy">{p.name}</div><div className="text-xs text-slate-400">{p.state || "—"}</div></div>
              </div>
              <div className="text-sm font-semibold text-navy">{p.completed} completed{p.rating ? ` · ★ ${p.rating}` : ""}</div>
            </div>
          ))}
          {a.top_performers.length === 0 && <div className="py-6 text-center text-sm text-slate-400">No completed assignments yet.</div>}
        </div>
      </div>
      <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h3 className="font-heading font-semibold text-navy">Geographic Coverage</h3>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="nc-analytics-geo">
          {a.geographic_coverage.map((g) => (
            <span key={g.state} className="px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-xs font-semibold text-navy">
              {g.state} <span className="text-cobalt">{g.count}</span>
            </span>
          ))}
          {a.geographic_coverage.length === 0 && <div className="text-sm text-slate-400">No coverage data yet.</div>}
        </div>
      </div>
    </div>
  );
}
